import { createSlice } from '@reduxjs/toolkit';
import { BASE_END_POINT_MESSAGES, MESSAGES_END_POINT } from '../../../config';
import { getLocalStorageAuthentication } from '../../../lib/auth';
import { messageUpdated } from '../app/appSlice';
import { Severity } from '../../../components/Message/Message';

const initialState = {
  conversations: [],
  currentConversation: null,
  messagesByConversation: {}, // Store messages by conversationId: { messages: [], loaded: boolean, loading: false }
  loading: false,
  error: null,
  conversationsLoading: false,
  conversationsError: null,
  sendingMessage: false,
  typingUsers: {}, // { conversationId: { userId: timestamp } }
  totalUnreadCount: 0,
  onlineUsers: {} // { userId: true } - tracks which users are online
};

const messagesSlice = createSlice({
  name: 'messages',
  initialState,
  reducers: {
    // Conversations
    fetchConversationsRequest(state) {
      state.conversationsLoading = true;
      state.conversationsError = null;
    },
    fetchConversationsSuccess(state, action) {
      state.conversationsLoading = false;
      state.conversations = action.payload.conversations;
      state.conversationsError = null;
      // Calculate total unread count
      state.totalUnreadCount = action.payload.conversations.reduce(
        (sum, conv) => sum + (conv.unreadCount || 0),
        0
      );
    },
    fetchConversationsFailure(state, action) {
      state.conversationsLoading = false;
      state.conversationsError = action.payload.error;
    },

    // Messages
    fetchMessagesRequest(state, action) {
      const conversationId = action.payload.conversationId;
      if (!state.messagesByConversation[conversationId]) {
        state.messagesByConversation[conversationId] = {
          messages: [],
          loaded: false,
          loading: true
        };
      } else {
        state.messagesByConversation[conversationId].loading = true;
      }
    },
    fetchMessagesSuccess(state, action) {
      const { conversationId, messages } = action.payload;
      state.messagesByConversation[conversationId] = {
        messages,
        loaded: true,
        loading: false
      };
    },
    fetchMessagesFailure(state, action) {
      const conversationId = action.payload.conversationId;
      if (state.messagesByConversation[conversationId]) {
        state.messagesByConversation[conversationId].loading = false;
      }
      state.error = action.payload.error;
    },

    // Send message
    sendMessageRequest(state) {
      state.sendingMessage = true;
    },
    sendMessageSuccess(state, action) {
      state.sendingMessage = false;
      const message = action.payload.message;
      const conversationId = message.conversationId;

      // Add message to the conversation's messages
      if (state.messagesByConversation[conversationId]) {
        state.messagesByConversation[conversationId].messages.push(message);
      }

      // Update conversation last message
      const conversation = state.conversations.find(
        (c) => c.id === conversationId
      );
      if (conversation) {
        conversation.lastMessage = message;
        conversation.lastMessageAt = message.createdAt;
      }
    },
    sendMessageFailure(state, action) {
      state.sendingMessage = false;
      state.error = action.payload.error;
    },

    // Set current conversation
    setCurrentConversation(state, action) {
      state.currentConversation = action.payload.conversation;
      // Don't clear messages - they're stored per conversation now
    },

    // Clear current conversation
    clearCurrentConversation(state) {
      state.currentConversation = null;
    },

    // Mark messages as read
    markMessagesAsReadSuccess(state, action) {
      const conversationId = action.payload.conversationId;
      // Update messages in the conversation
      if (state.messagesByConversation[conversationId]) {
        state.messagesByConversation[conversationId].messages =
          state.messagesByConversation[conversationId].messages.map((msg) => ({
            ...msg,
            isRead: true
          }));
      }
      // Update conversation unread count in conversations array
      const conversation = state.conversations.find(
        (c) => c.id === conversationId
      );
      if (conversation) {
        const previousUnread = conversation.unreadCount || 0;
        conversation.unreadCount = 0;
        state.totalUnreadCount = Math.max(
          0,
          state.totalUnreadCount - previousUnread
        );
      }
      // Also update currentConversation if it's the same conversation
      if (state.currentConversation && state.currentConversation.id === conversationId) {
        state.currentConversation.unreadCount = 0;
      }
    },

    // Receive new message via WebSocket
    receiveNewMessage(state, action) {
      const message = action.payload.message;
      const conversationId = message.conversationId;

      // Check if user is currently on the Messages page viewing this conversation
      // We check both: 1) if on /messages route, and 2) if viewing this specific conversation
      const isOnMessagesPage = window.location.pathname.includes('/messages');
      const isViewingConversation =
        isOnMessagesPage &&
        state.currentConversation &&
        state.currentConversation.id === conversationId;

      console.log('🔔 [Redux] receiveNewMessage:', {
        conversationId,
        isOnMessagesPage,
        currentConversationId: state.currentConversation?.id,
        isViewingConversation
      });

      // Add message to conversation's messages if loaded
      if (state.messagesByConversation[conversationId]) {
        state.messagesByConversation[conversationId].messages.push(message);
      }

      // Update conversation last message if conversation is loaded
      const conversation = state.conversations.find(
        (c) => c.id === conversationId
      );
      
      if (conversation) {
        conversation.lastMessage = message;
        conversation.lastMessageAt = message.createdAt;
        // Increment unread if not viewing this conversation
        if (!isViewingConversation) {
          conversation.unreadCount = (conversation.unreadCount || 0) + 1;
          state.totalUnreadCount += 1;
          console.log('🔔 [Redux] Incremented unread count to:', state.totalUnreadCount);
        } else {
          console.log('🔔 [Redux] Not incrementing - user is viewing conversation');
        }
      } else {
        // Conversation not loaded yet - still increment total unread count
        // This ensures the badge updates even if user hasn't loaded messages
        if (!isViewingConversation) {
          state.totalUnreadCount += 1;
          console.log('🔔 [Redux] Incremented unread count (no conversation) to:', state.totalUnreadCount);
        }
      }
    },

    // Receive read receipt via WebSocket
    receiveReadReceipt(state, action) {
      const conversationId = action.payload.conversationId;
      // Update messages in conversation if loaded
      if (state.messagesByConversation[conversationId]) {
        state.messagesByConversation[conversationId].messages =
          state.messagesByConversation[conversationId].messages.map((msg) => ({
            ...msg,
            isRead: true
          }));
      }
    },

    // Typing indicators
    setTypingUser(state, action) {
      const { conversationId, userId } = action.payload;
      if (!state.typingUsers[conversationId]) {
        state.typingUsers[conversationId] = {};
      }
      state.typingUsers[conversationId][userId] = Date.now();
    },
    removeTypingUser(state, action) {
      const { conversationId, userId } = action.payload;
      if (state.typingUsers[conversationId]) {
        delete state.typingUsers[conversationId][userId];
      }
    },
    clearTypingUsers(state, action) {
      const { conversationId } = action.payload;
      delete state.typingUsers[conversationId];
    },

    // Start conversation
    startConversationSuccess(state, action) {
      const conversation = action.payload.conversation;
      // Add to conversations if not already there
      const exists = state.conversations.find((c) => c.id === conversation.id);
      if (!exists) {
        state.conversations.unshift(conversation);
      }
      state.currentConversation = conversation;
    },

    // Update unread count (from API or WebSocket)
    updateUnreadCount(state, action) {
      state.totalUnreadCount = action.payload.count;
    },

    // Increment unread count (for new message notifications)
    incrementUnreadCount(state) {
      state.totalUnreadCount += 1;
    },

    // Set initial online users (received when connecting)
    setOnlineUsers(state, action) {
      const userIds = action.payload.userIds;
      state.onlineUsers = {};
      userIds.forEach((userId) => {
        state.onlineUsers[userId] = true;
      });
      console.log('🟢 [Redux] Set online users:', userIds.length);
    },

    // Update single user presence
    updateUserPresence(state, action) {
      const { userId, isOnline } = action.payload;
      if (isOnline) {
        state.onlineUsers[userId] = true;
        console.log('🟢 [Redux] User came online:', userId);
      } else {
        delete state.onlineUsers[userId];
        console.log('⚫ [Redux] User went offline:', userId);
      }
    }
  }
});

// Selectors
export const conversationsSelector = (state) => state.messages.conversations;
export const currentConversationSelector = (state) =>
  state.messages.currentConversation;
export const messagesSelector = (state) => {
  const currentConversation = state.messages.currentConversation;
  if (!currentConversation?.id) return [];
  const conversationData =
    state.messages.messagesByConversation[currentConversation.id];
  return conversationData?.messages || [];
};
export const conversationsLoadingSelector = (state) =>
  state.messages.conversationsLoading;
export const messagesLoadingSelector = (state) => {
  const currentConversation = state.messages.currentConversation;
  if (!currentConversation?.id) return false;
  const conversationData =
    state.messages.messagesByConversation[currentConversation.id];
  return conversationData?.loading || false;
};
export const sendingMessageSelector = (state) => state.messages.sendingMessage;
export const typingUsersSelector = (conversationId) => (state) =>
  state.messages.typingUsers[conversationId] || {};
export const totalUnreadCountSelector = (state) =>
  state.messages.totalUnreadCount;
export const hasLoadedConversationSelector = (conversationId) => (state) => {
  if (!conversationId) return false;
  const conversationData =
    state.messages.messagesByConversation[conversationId];
  return conversationData?.loaded || false;
};
export const onlineUsersSelector = (state) => state.messages.onlineUsers;
export const isUserOnlineSelector = (userId) => (state) =>
  !!state.messages.onlineUsers[userId];

// Actions
export const {
  fetchConversationsRequest,
  fetchConversationsSuccess,
  fetchConversationsFailure,
  fetchMessagesRequest,
  fetchMessagesSuccess,
  fetchMessagesFailure,
  sendMessageRequest,
  sendMessageSuccess,
  sendMessageFailure,
  setCurrentConversation,
  clearCurrentConversation,
  markMessagesAsReadSuccess,
  receiveNewMessage,
  receiveReadReceipt,
  setTypingUser,
  removeTypingUser,
  clearTypingUsers,
  startConversationSuccess,
  updateUnreadCount,
  incrementUnreadCount,
  setOnlineUsers,
  updateUserPresence
} = messagesSlice.actions;

// Thunk to fetch conversations
export const fetchConversations = () => async (dispatch) => {
  console.log('🔍 [Messages Thunk] Fetching conversations');
  dispatch(fetchConversationsRequest());

  const localAuth = getLocalStorageAuthentication();
  const url = `${BASE_END_POINT_MESSAGES}${MESSAGES_END_POINT}/conversations`;

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localAuth.token}`
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log('✅ [Messages Thunk] Conversations fetched:', data.data.length);
    dispatch(fetchConversationsSuccess({ conversations: data.data || [] }));
    return data.data;
  } catch (error) {
    console.error('❌ [Messages Thunk] Failed to fetch conversations:', error);
    dispatch(fetchConversationsFailure({ error: error.message }));
    throw error;
  }
};

// Thunk to fetch unread count (lightweight, for badge display)
export const fetchUnreadCount = () => async (dispatch) => {
  const localAuth = getLocalStorageAuthentication();
  if (!localAuth?.token) {
    console.log('🔕 [Messages Thunk] No auth token, skipping unread count fetch');
    return;
  }

  const url = `${BASE_END_POINT_MESSAGES}${MESSAGES_END_POINT}/unread-count`;

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localAuth.token}`
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    const count = data.data || 0;
    console.log('🔔 [Messages Thunk] Unread count:', count);
    dispatch(updateUnreadCount({ count }));
    return count;
  } catch (error) {
    console.error('❌ [Messages Thunk] Failed to fetch unread count:', error);
    // Don't throw - this is a non-critical operation
    return 0;
  }
};

// Thunk to fetch messages for a conversation
export const fetchMessages =
  ({ conversationId }) =>
  (dispatch, getState) => {
    // Check if already loading or loaded - prevent duplicate fetches
    const state = getState();
    const conversationData =
      state.messages.messagesByConversation[conversationId];

    if (conversationData?.loading) {
      console.log(
        '⏳ [Messages Thunk] Already loading, skipping:',
        conversationId
      );
      return Promise.resolve({ skipped: true });
    }

    if (conversationData?.loaded) {
      console.log(
        '✅ [Messages Thunk] Already loaded, skipping:',
        conversationId
      );
      return Promise.resolve({
        skipped: true,
        messages: conversationData.messages
      });
    }

    console.log('🔍 [Messages Thunk] Fetching messages for:', conversationId);
    dispatch(fetchMessagesRequest({ conversationId }));

    const localAuth = getLocalStorageAuthentication();
    const url = `${BASE_END_POINT_MESSAGES}${MESSAGES_END_POINT}/conversation/${conversationId}`;

    return fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localAuth.token}`
      }
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        console.log(
          '✅ [Messages Thunk] Messages fetched:',
          data.data?.length || 0
        );
        dispatch(
          fetchMessagesSuccess({ conversationId, messages: data.data || [] })
        );
        return { success: true, messages: data.data };
      })
      .catch((error) => {
        console.error('❌ [Messages Thunk] Failed to fetch messages:', error);
        dispatch(
          fetchMessagesFailure({ conversationId, error: error.message })
        );
        return { success: false, error: error.message };
      });
  };

// Thunk to send a message (NO await dispatch)
export const sendMessage =
  ({ conversationId, content }) =>
  (dispatch) => {
    console.log('📤 [Messages Thunk] Sending message');
    dispatch(sendMessageRequest());

    const localAuth = getLocalStorageAuthentication();
    const url = `${BASE_END_POINT_MESSAGES}${MESSAGES_END_POINT}/send`;

    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localAuth.token}`
      },
      body: JSON.stringify({ conversationId, content })
    })
      .then((response) => {
        if (!response.ok) {
          return response.text().then((text) => {
            let errorMsg = `HTTP error! status: ${response.status}`;
            try {
              const data = JSON.parse(text);
              errorMsg = data?.message || errorMsg;
            } catch (e) {
              errorMsg = text || errorMsg;
            }
            throw new Error(errorMsg);
          });
        }
        return response.json();
      })
      .then((data) => {
        console.log('✅ [Messages Thunk] Message sent successfully');
        dispatch(sendMessageSuccess({ message: data.data }));
        return data.data;
      })
      .catch((error) => {
        console.error('❌ [Messages Thunk] Failed to send message:', error);
        dispatch(sendMessageFailure({ error: error.message }));
        dispatch(
          messageUpdated({
            text: error.message || 'Failed to send message. Please try again.',
            severity: Severity.ERROR
          })
        );
        return { success: false, error: error.message };
      });
  };

// Thunk to mark messages as read
export const markMessagesAsRead =
  ({ conversationId }) =>
  (dispatch) => {
    console.log(
      '📖 [Messages Thunk] Marking messages as read:',
      conversationId
    );

    const localAuth = getLocalStorageAuthentication();
    const url = `${BASE_END_POINT_MESSAGES}${MESSAGES_END_POINT}/conversation/${conversationId}/read`;

    return fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localAuth.token}`
      }
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then(() => {
        console.log('✅ [Messages Thunk] Messages marked as read');
        dispatch(markMessagesAsReadSuccess({ conversationId }));
      })
      .catch((error) => {
        console.error('❌ [Messages Thunk] Failed to mark as read:', error);
      });
  };

// Thunk to start a new conversation
export const startConversation =
  ({ recipientUserId, initialMessage }) =>
  (dispatch) => {
    console.log(
      '🆕 [Messages Thunk] Starting conversation with:',
      recipientUserId
    );

    const localAuth = getLocalStorageAuthentication();
    const url = `${BASE_END_POINT_MESSAGES}${MESSAGES_END_POINT}/conversation/start`;

    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localAuth.token}`
      },
      body: JSON.stringify({ recipientUserId, initialMessage })
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        console.log('✅ [Messages Thunk] Conversation started');
        dispatch(startConversationSuccess({ conversation: data.data }));
        dispatch(
          messageUpdated({
            text: 'Conversation started successfully',
            severity: Severity.SUCCESS
          })
        );
        return data.data;
      })
      .catch((error) => {
        console.error(
          '❌ [Messages Thunk] Failed to start conversation:',
          error
        );
        dispatch(
          messageUpdated({
            text: 'Failed to start conversation. Please try again.',
            severity: Severity.ERROR
          })
        );
        throw error;
      });
  };

export default messagesSlice.reducer;
